import type { ComponentType, SVGProps } from "react";

import Add from "./icons/add.svg?react";
import Alert from "./icons/alert.svg?react";
import Android from "./icons/android.svg?react";
import ArrowDownS from "./icons/arrow-down-s.svg?react";
import ArrowLeftS from "./icons/arrow-left-s.svg?react";
import ArrowLeft from "./icons/arrow-left.svg?react";
import ArrowRightS from "./icons/arrow-right-s.svg?react";
import ArrowUpS from "./icons/arrow-up-s.svg?react";
import Close from "./icons/close.svg?react";
import Counter from "./icons/counter.svg?react";
import Download4 from "./icons/download-4.svg?react";
import ExternalLink from "./icons/external-link.svg?react";
import Home6 from "./icons/home-6.svg?react";
import Home from "./icons/home.svg?react";
import ImageAdd from "./icons/image-add.svg?react";
import InformationFilled from "./icons/information-filled.svg?react";
import Information from "./icons/information.svg?react";
import Ios from "./icons/ios.svg?react";
import Loader2 from "./icons/loader-2.svg?react";
import Loader4 from "./icons/loader-4.svg?react";
import LogoutBoxR from "./icons/logout-box-r.svg?react";
import MailSend from "./icons/mail-send.svg?react";
import Menu from "./icons/menu.svg?react";
import Message2 from "./icons/message-2.svg?react";
import Message3 from "./icons/message-3.svg?react";
import News from "./icons/news.svg?react";
import Notification3Filled from "./icons/notification-3-filled.svg?react";
import Notification3 from "./icons/notification-3.svg?react";
import Phone from "./icons/phone.svg?react";
import QuestionAnswerFilled from "./icons/question-answer-filled.svg?react";
import Search from "./icons/search.svg?react";
import Settings4 from "./icons/settings-4.svg?react";
import Spam2Filled from "./icons/spam-2-filled.svg?react";
import Team from "./icons/team.svg?react";
import Telegram2Filled from "./icons/telegram-2-filled.svg?react";
import Telegram2 from "./icons/telegram-2.svg?react";
import VKontakte from "./icons/vkontakte.svg?react";
import Web from "./icons/web.svg?react";
import WhatsappFilled from "./icons/whatsapp-filled.svg?react";
import Whatsapp from "./icons/whatsapp.svg?react";

export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export const EAGER_ICON_TYPE_MAP = {
  add: Add,
  alert: Alert,
  android: Android,
  "arrow-down-s": ArrowDownS,
  "arrow-left-s": ArrowLeftS,
  "arrow-left": ArrowLeft,
  "arrow-right-s": ArrowRightS,
  "arrow-up-s": ArrowUpS,
  close: Close,
  counter: Counter,
  "download-4": Download4,
  "external-link": ExternalLink,
  "home-6": Home6,
  home: Home,
  "image-add": ImageAdd,
  "information-filled": InformationFilled,
  information: Information,
  ios: Ios,
  "loader-2": Loader2,
  "loader-4": Loader4,
  "logout-box-r": LogoutBoxR,
  "mail-send": MailSend,
  menu: Menu,
  "message-2": Message2,
  "message-3": Message3,
  news: News,
  "notification-3-filled": Notification3Filled,
  "notification-3": Notification3,
  phone: Phone,
  "question-answer-filled": QuestionAnswerFilled,
  search: Search,
  "settings-4": Settings4,
  "spam-2-filled": Spam2Filled,
  team: Team,
  "telegram-2-filled": Telegram2Filled,
  "telegram-2": Telegram2,
  web: Web,
  vk: VKontakte,
  "whatsapp-filled": WhatsappFilled,
  whatsapp: Whatsapp,
} satisfies Record<string, IconComponent>;

export const LAZY_ICON_LOADERS = {
  archive: () => import("./icons/archive.svg?react").then((mod) => mod.default),
  "attachment-2": () =>
    import("./icons/attachment-2.svg?react").then((mod) => mod.default),
  "bar-chart-horizontal-filled": () =>
    import("./icons/bar-chart-horizontal-filled.svg?react").then(
      (mod) => mod.default,
    ),
  build: () => import("./icons/build.svg?react").then((mod) => mod.default),
  calendar: () =>
    import("./icons/calendar.svg?react").then((mod) => mod.default),
  camera: () => import("./icons/camera.svg?react").then((mod) => mod.default),
  chat: () => import("./icons/chat.svg?react").then((mod) => mod.default),
  check: () => import("./icons/check.svg?react").then((mod) => mod.default),
  "checkbox-circle-filled": () =>
    import("./icons/checkbox-circle-filled.svg?react").then(
      (mod) => mod.default,
    ),
  computer: () =>
    import("./icons/computer.svg?react").then((mod) => mod.default),
  crop: () => import("./icons/crop.svg?react").then((mod) => mod.default),
  "delete-bin-6": () =>
    import("./icons/delete-bin-6.svg?react").then((mod) => mod.default),
  device: () => import("./icons/device.svg?react").then((mod) => mod.default),
  discuss: () => import("./icons/discuss.svg?react").then((mod) => mod.default),
  document: () =>
    import("./icons/document.svg?react").then((mod) => mod.default),
  equalizer: () =>
    import("./icons/equalizer.svg?react").then((mod) => mod.default),
  "exchange-dollar": () =>
    import("./icons/exchange-dollar.svg?react").then((mod) => mod.default),
  "eye-close": () =>
    import("./icons/eye-close.svg?react").then((mod) => mod.default),
  "hotel-bed-filled": () =>
    import("./icons/hotel-bed-filled.svg?react").then((mod) => mod.default),
  "indeterminate-circle-filled": () =>
    import("./icons/indeterminate-circle-filled.svg?react").then(
      (mod) => mod.default,
    ),
  "layout-2-filled": () =>
    import("./icons/layout-2-filled.svg?react").then((mod) => mod.default),
  "layout-4": () =>
    import("./icons/layout-4.svg?react").then((mod) => mod.default),
  "layout-column": () =>
    import("./icons/layout-column.svg?react").then((mod) => mod.default),
  mic: () => import("./icons/mic.svg?react").then((mod) => mod.default),
  pencil: () => import("./icons/pencil.svg?react").then((mod) => mod.default),
  "phone-filled": () =>
    import("./icons/phone-filled.svg?react").then((mod) => mod.default),
  "pine-filled": () =>
    import("./icons/pine-filled.svg?react").then((mod) => mod.default),
  "pushpin-filled": () =>
    import("./icons/pushpin-filled.svg?react").then((mod) => mod.default),
  "question-filled": () =>
    import("./icons/question-filled.svg?react").then((mod) => mod.default),
  refresh: () => import("./icons/refresh.svg?react").then((mod) => mod.default),
  "restaurant-2-filled": () =>
    import("./icons/restaurant-2-filled.svg?react").then((mod) => mod.default),
  "rounded-corner": () =>
    import("./icons/rounded-corner.svg?react").then((mod) => mod.default),
  "settings-4-filled": () =>
    import("./icons/settings-4-filled.svg?react").then((mod) => mod.default),
  "share-box": () =>
    import("./icons/share-box.svg?react").then((mod) => mod.default),
  "shopping-cart-2-filled": () =>
    import("./icons/shopping-cart-2-filled.svg?react").then(
      (mod) => mod.default,
    ),
  "spam-2": () => import("./icons/spam-2.svg?react").then((mod) => mod.default),
  time: () => import("./icons/time.svg?react").then((mod) => mod.default),
  twentyfive: () =>
    import("./icons/twenty-five.svg?react").then((mod) => mod.default),
} satisfies Record<string, () => Promise<IconComponent>>;

export type IconName =
  | keyof typeof EAGER_ICON_TYPE_MAP
  | keyof typeof LAZY_ICON_LOADERS;

export const isEagerIconName = (
  name: IconName,
): name is keyof typeof EAGER_ICON_TYPE_MAP => name in EAGER_ICON_TYPE_MAP;
